import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { createApi, formatApiError, formatDate } from "@/lib/api";
import { Loading, StateBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { clearStoredSelection } from "./patientContext";

export default function Activate() {
  const { token } = useParams();
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [terms, setTerms] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const api = createApi("apoio");
    (async () => {
      try {
        const [{ data }, termsResp] = await Promise.all([
          api.client.get(`/auth/invite/${token}`),
          api.client.get("/terms/current"),
        ]);
        setInvite(data);
        setTerms(termsResp.data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSubmitting(true);
    const api = createApi("apoio");
    try {
      const { data } = await api.client.post("/auth/activate", { token, password, accept_terms: accepted });
      clearStoredSelection();
      adoptSession(data);
      toast.success("Conta ativada. Bem-vinda!");
      navigate("/apoio", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSubmitting(false);
  };

  if (error)
    return (
      <div className="mx-auto max-w-md p-8">
        <StateBlock testId="activate-error" icon={ShieldCheck} title="Convite indisponível" description={error} />
        <Button data-testid="activate-go-login" onClick={() => navigate("/apoio/login")} className="mt-6 rounded-full bg-sage-600 text-bone">
          Ir para o login
        </Button>
      </div>
    );

  if (!invite) return <Loading label="Validando convite…" />;

  return (
    <div data-testid="activate-page" className="min-h-screen bg-bone">
      <div className="mx-auto max-w-md px-6 pb-28 pt-14">
        <p className="er-label">Ativação de conta</p>
        <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-ink-900">Olá, {invite.name}</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink-500">
          Você foi autorizada a acompanhar <strong>{invite.patient_name}</strong>. Este convite é de uso único e expira em{" "}
          {formatDate(invite.expires_at)}.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <Label className="text-sm text-ink-600">Seu e-mail</Label>
            <Input data-testid="activate-email" value={invite.email} readOnly className="mt-2 h-12 rounded-xl bg-sand" />
          </div>
          <div>
            <Label htmlFor="new-password" className="text-sm text-ink-600">
              Crie sua senha (mín. 8 caracteres)
            </Label>
            <Input
              id="new-password"
              data-testid="activate-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 rounded-xl bg-white"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-sm text-ink-600">
              Confirme a senha
            </Label>
            <Input
              id="confirm-password"
              data-testid="activate-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 h-12 rounded-xl bg-white"
              minLength={8}
              required
            />
          </div>

          <div className="rounded-2xl border border-border bg-white p-5">
            <p className="er-label">Termos versão {invite.terms_version}</p>
            <h3 className="mt-2 font-display text-base font-medium text-ink-900">{terms?.title}</h3>
            <p className="mt-3 max-h-40 overflow-y-auto text-sm leading-relaxed text-ink-500">{terms?.body}</p>
            <label className="mt-4 flex items-start gap-3 text-sm text-ink-600">
              <Checkbox data-testid="activate-terms-checkbox" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} />
              Li e aceito os termos de uso e sigilo desta versão.
            </label>
          </div>

          <Button
            type="submit"
            data-testid="activate-submit"
            disabled={submitting || !accepted}
            className="h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700"
          >
            {submitting ? "Ativando…" : "Ativar conta e entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
